import { Injectable } from '@angular/core';
import { catchError, map, tap } from 'rxjs/operators';
import { ALLOX_ROLE_KEY } from '../constants/allox-constant';
import { AlloxApiService } from './allox-api.service';
import { AuthMFA, LoginCredential } from '../models';
import { PasswordAuth, AuthCredential } from '../models';
import { ChangePassword, ResetPassword } from '../models';
import { MFARegister } from '../models/mfa-register.model';
import { BehaviorSubject, of } from 'rxjs';
import { NextAction, AlloxApiErrorCode } from '@app/core';
import { DialogService } from '@app/core/services/dialog.service';

@Injectable({ providedIn: 'root' })
export class AuthService {

  private _nextAction = new BehaviorSubject<string>('');
  private _errorMessage = new BehaviorSubject<string>('');
  public errorMessage$ = this._errorMessage.asObservable();

  get nextAction() {
    return this._nextAction.asObservable();
  }

  public get accessRole() {
    return localStorage.getItem(ALLOX_ROLE_KEY) || null;
  }

  private isShowConfirmDialog = false;

  /**
   * コンストラクタ
   */
  constructor(
    private http: AlloxApiService,
    private dialogService: DialogService
  ) { }

  /**
   * MFA認証
   * @remarks
   * サーバ処理を呼び出してMFAコードによる認証を行う。
   * （セッション有効期限切れエラー）セッション切れのポップアップを表示しログイン画面へ戻る。
   * （セッション有効期限切れエラー以外）ログイン画面へ遷移するための情報とエラーメッセージを設定する。
   * @param item MFA認証
   * @returns 実行結果
   */
  public authenticateMFA(item: AuthMFA) {
    const json = item.toJSON();
    return this.http.post<LoginCredential>('/auth/mfa/login', json).pipe(
      map(obj => {
        const res = new LoginCredential(obj);
        this._nextAction.next(res.nextAction);
        return res;
      }),
      catchError(err => {
        if (err.errorCode === AlloxApiErrorCode.AUTH_SESSION_EXPIRED) {
          // NOTE: api-error-handler.service.tsのrunSessionErrorActionと同じ処理である。
          // 本来はapi-error-handler.service.tsの処理を呼び出すべきだが、
          // api-error-handler.service.tsが既にauth.serviceを呼び出しており循環参照となる。
          // auth-mfa.component.tsで実装するとrunSessionErrorAction()は呼び出せるが、
          // セッション以外の認証エラーの場合にログイン画面へ遷移させるためには
          // login.component.tsが持つnextActionに値を設定し、auth.service.tsが持つerrorMessageに値を設定する必要があり
          // 逆に処理が複雑になってしまうためにapi-error-handler.service.tsの処理を移植している。
          this.runSessionErrorAction(err.message);
        } else {
          this._nextAction.next(NextAction.AUTH_PW);
          this._errorMessage.next(err.message);
        }
        return of(null);
      })
    );
  }

  /**
   * セッションエラー
   * @remarks
   * セッションエラーである旨のポップアップを表示しOKが押下されたらログイン画面に戻る
   * @param message ポップアップメッセージ
   */
  private runSessionErrorAction(message: string) {
    if (!this.isShowConfirmDialog) {
      this.isShowConfirmDialog = true;
      const size = 'modal-sm';
      const modal = this.dialogService.openAlert(message, size);
      const content: any = modal.content;
      content.onClose.subscribe(_ => {
        this.removeAlloxRole();
        window.location.replace(location.pathname);
        this.isShowConfirmDialog = false;
      });
    }
  }

  /**
   * ログイン
   * @remarks
   * サーバ処理を呼び出してログインを行う。
   * @param item パスワード認証
   * @returns 実行結果
   */
  public login(item: PasswordAuth) {
    const json = item.toJSON();
    return this.http.post<AuthCredential>('/auth/password/login', json).pipe(
      map(obj => {
        const res = new AuthCredential(obj);
        this._nextAction.next(res.next_action);
        return res;
      }),
      catchError(err => {
        this._nextAction.next(NextAction.AUTH_PW);
        this._errorMessage.next(err.message);
        return of(null);
      })
    );
}

  /**
   * ログアウト
   * @remarks
   * サーバ処理を呼び出してログアウトを行う。
   * @returns 実行結果
   */
  public logout() {
    return this.http.get('/logout').pipe(
      tap(() => localStorage.removeItem(ALLOX_ROLE_KEY))
    );
  }

  public removeAlloxRole() {
    localStorage.removeItem(ALLOX_ROLE_KEY);
  }
  /**
   * 仮パスワード発行
   * @remarks
   * サーバ処理を呼び出して、仮パスワードを発行する。
   * @param item 仮パスワード
   * @returns 実行結果
   */
  public putResetPassword(item: ResetPassword) {
    const json = item.toJSON();
    return this.http.put('/auth/password/reset', json);
  }

  /**
   * 仮パスワード変更
   * @remarks
   * サーバ処理を呼び出して、パスワード変更を行う。
   * @param item 変更後のパスワード
   * @returns 実行結果
   */
  public putChangePassword(item: ChangePassword){
    const json = item.toJSON();
    return this.http.put<AuthCredential>('/auth/password/change', json).pipe(
      map(obj => {
        const res = new AuthCredential(obj);
        this._nextAction.next(res.next_action);
        return res;
      })
    );
  }

  /**
   * MFAデバイス登録
   * @remarks
   * サーバ処理を呼び出してMFAデバイス登録を行う。
   * @param item MFAデバイス登録情報({@link MFARegister})
   * @returns 実行結果({@link LoginCredential})
   */
  public registerMFADevice(item: MFARegister) {
    const json = item.toJSON();
    return this.http.post<LoginCredential>('/auth/mfa/device', json).pipe(
      map(obj => {
        const res = new LoginCredential(obj);
        this._nextAction.next(res.nextAction);
        return res;
      })
    );
  }
}
